// src/pages/EventPage.js

import React, { useState, useEffect } from 'react';
import CastForm from '../components/CastForm';
import CastList from '../components/CastList';
import GuestForm from '../components/GuestForm';
import GuestList from '../components/GuestList';
import { useEvents, useCasts, useGuests, useMatchingResults } from '../hooks/useFirebase';
import { useSchedule, nextEventDate } from '../hooks/useSchedule';
import DraftPickerModal from '../components/DraftPickerModal';
import { useConfirm } from '../contexts/ConfirmContext';
import FormBuilder from '../components/FormBuilder';
import { useApplicationForm } from '../hooks/useApplicationForm';
import { useApplicantStats, guestIdentityKey } from '../hooks/useApplicantStats';
import { useHistoryAccess } from '../hooks/useHistoryAccess';
import ApplicantHistoryModal from '../components/ApplicantHistoryModal';

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_STEPS_BY_TYPE = {
  matching: [
    { key: 'waiting',  label: '準備中',     desc: 'キャスト登録・出勤確認',         color: '#f59e0b', pulseColor: '245,158,11' },
    { key: 'matching', label: '指名受付中',  desc: 'キャスト・ゲストが希望を選択',    color: '#6c63ff', pulseColor: '108,99,255' },
    { key: 'matched',  label: 'マッチ完了',  desc: 'マッチング結果が確定済み',        color: '#10b981', pulseColor: '16,185,129' },
  ],
  raffle: [
    { key: 'waiting',  label: '準備中',     desc: 'フォーム準備・定員設定',          color: '#f59e0b', pulseColor: '245,158,11' },
    { key: 'matching', label: '応募受付中',  desc: '応募フォームで受付中',            color: '#6c63ff', pulseColor: '108,99,255' },
    { key: 'matched',  label: '抽選完了',   desc: '当選者が確定',                   color: '#10b981', pulseColor: '16,185,129' },
  ],
  open: [
    { key: 'waiting',  label: '準備中',     desc: 'フォーム準備・定員設定',          color: '#f59e0b', pulseColor: '245,158,11' },
    { key: 'matching', label: '応募受付中',  desc: '応募フォームで受付中',            color: '#6c63ff', pulseColor: '108,99,255' },
    { key: 'matched',  label: '受付完了',   desc: '参加者が確定',                   color: '#10b981', pulseColor: '16,185,129' },
  ],
  cast_instance: [
    { key: 'waiting',  label: '準備中',       desc: 'キャスト登録・出欠確認',        color: '#f59e0b', pulseColor: '245,158,11' },
    { key: 'matching', label: '抽選準備中',    desc: '抽選実行タブで割り振りを実行',  color: '#6c63ff', pulseColor: '108,99,255' },
    { key: 'matched',  label: '割り振り完了',  desc: 'インスタンス割り振りが確定',    color: '#10b981', pulseColor: '16,185,129' },
  ],
};
const getStatusSteps = (eventType) => STATUS_STEPS_BY_TYPE[eventType] ?? STATUS_STEPS_BY_TYPE.matching;

// ─── Phase guide content ──────────────────────────────────────────────────────

const getPhaseGuide = (status, matchingMode, eventType = 'matching', hasResults = false) => {
  const mode = matchingMode || 'cast_select';

  const guidesByType = {
    cast_instance: {
      waiting: {
        icon: '📋', title: '準備フェーズ',
        color: '#f59e0b', bg: '#fffbeb', border: '#fde68a',
        tasks: [
          'キャストを登録し、出欠（部ごとの出勤）を確認する',
          '準備ができたら抽選準備を開始する',
        ],
        cta: '準備完了 — 抽選準備を開始する',
        nextStatus: 'matching', navigateToMatching: false,
      },
      matching: {
        icon: '🏘️', title: '抽選準備フェーズ',
        color: '#6c63ff', bg: '#f5f3ff', border: '#c4b5fd',
        tasks: [
          '抽選実行タブでインスタンス割り振りを実行する',
          '割り振りが完了したら完了ステータスへ進む',
        ],
        cta: '抽選実行タブへ移動する',
        nextStatus: 'matching', navigateToMatching: true,
      },
      matched: {
        icon: '🎉', title: '割り振り完了',
        color: '#10b981', bg: '#ecfdf5', border: '#6ee7b7',
        tasks: [
          '抽選実行タブでインスタンスごとのキャスト一覧を確認する',
          'キャストへ各自のインスタンス番号を案内する',
          'イベント終了後は下のボタンで最初に戻せる',
        ],
        cta: 'イベントを終了して最初に戻す',
        nextStatus: 'waiting', navigateToMatching: false,
      },
    },
    matching: {
      waiting: {
        icon: '📋', title: '準備フェーズ',
        color: '#f59e0b', bg: '#fffbeb', border: '#fde68a',
        tasks: [
          '応募フォームを作成する（ヘッダーの「フォームを作成」から）',
          'キャストを登録し、出勤状態を確認する',
          '手動でゲストを追加する場合は下のゲスト欄から登録する',
          '優先したい組み合わせがあれば優先度を「優先保証」に設定する',
        ],
        cta: '準備完了 — 応募受付を開始する',
        nextStatus: 'matching', navigateToMatching: false,
      },
      matching: mode === 'random_only' ? {
        icon: '🎯', title: '応募受付フェーズ',
        color: '#6c63ff', bg: '#f5f3ff', border: '#c4b5fd',
        tasks: [
          '応募フォームのURLをゲストに公開する',
          '応募状況を確認する',
          '締め切ったら抽選実行タブで抽選を実行する',
        ],
        cta: '応募を締め切って抽選へ進む',
        nextStatus: 'matched', navigateToMatching: true,
      } : {
        icon: '🎯', title: '応募受付フェーズ',
        color: '#6c63ff', bg: '#f5f3ff', border: '#c4b5fd',
        tasks: [
          '応募フォームのURLをゲストに公開する',
          '応募状況を確認する',
          '締め切ると指名設定フェーズへ移行します',
        ],
        cta: '応募を締め切って指名フェーズへ進む',
        nextStatus: 'matched', navigateToMatching: false,
      },
      matched: (mode === 'random_only' || hasResults) ? {
        icon: '🎉', title: 'マッチング完了',
        color: '#10b981', bg: '#ecfdf5', border: '#6ee7b7',
        tasks: [
          '抽選実行タブでマッチング結果と部屋番号を確認する',
          'キャストとゲストへ各自の部屋番号を案内する',
          'イベント終了後は下のボタンで状態を最初に戻せる',
        ],
        cta: 'イベントを終了して最初に戻す',
        nextStatus: 'waiting', navigateToMatching: false,
      } : {
        icon: '📝', title: '指名設定フェーズ',
        color: '#8b5cf6', bg: '#f5f3ff', border: '#c4b5fd',
        tasks: [
          'ヘッダーの指名URLを発行してキャスト・ゲストに共有する',
          'キャスト・ゲストが各自の希望を設定するのを待つ',
          '設定が完了したら抽選実行タブで抽選を実行する',
          'イベント終了時はステッパーで準備中に戻す',
        ],
        cta: '抽選実行タブへ移動する',
        nextStatus: 'matched', navigateToMatching: true,
      },
    },
    raffle: {
      waiting: {
        icon: '📋', title: '準備フェーズ',
        color: '#f59e0b', bg: '#fffbeb', border: '#fde68a',
        tasks: [
          'ヘッダーの定員欄で定員を設定・確認する',
          '応募フォームを作成・公開する',
        ],
        cta: '準備完了 — 応募受付を開始する',
        nextStatus: 'matching', navigateToMatching: false,
      },
      matching: {
        icon: '🎯', title: '応募受付フェーズ',
        color: '#6c63ff', bg: '#f5f3ff', border: '#c4b5fd',
        tasks: [
          '応募フォームのURLをゲストに公開する',
          '応募一覧で応募状況を確認する',
          '締め切ったら抽選実行タブで抽選を実行する',
        ],
        cta: '応募を締め切って抽選へ進む',
        nextStatus: 'matched', navigateToMatching: true,
      },
      matched: {
        icon: '🎉', title: '抽選完了',
        color: '#10b981', bg: '#ecfdf5', border: '#6ee7b7',
        tasks: [
          '抽選実行タブで当選者と落選者を確認する',
          'ゲストへ当落結果を連絡する',
          'イベント終了後は下のボタンで状態を最初に戻せる',
        ],
        cta: 'イベントを終了して最初に戻す',
        nextStatus: 'waiting', navigateToMatching: false,
      },
    },
    open: {
      waiting: {
        icon: '📋', title: '準備フェーズ',
        color: '#f59e0b', bg: '#fffbeb', border: '#fde68a',
        tasks: [
          'ヘッダーの定員欄で定員を設定・確認する',
          '応募フォームを作成・公開する',
        ],
        cta: '準備完了 — 応募受付を開始する',
        nextStatus: 'matching', navigateToMatching: false,
      },
      matching: {
        icon: '🎯', title: '応募受付フェーズ',
        color: '#6c63ff', bg: '#f5f3ff', border: '#c4b5fd',
        tasks: [
          '応募フォームのURLをゲストに公開する',
          '応募状況を確認する',
          '締め切ったら抽選実行タブで先着確定を実行する',
        ],
        cta: '応募を締め切って確定へ進む',
        nextStatus: 'matched', navigateToMatching: true,
      },
      matched: {
        icon: '🎉', title: '受付完了',
        color: '#10b981', bg: '#ecfdf5', border: '#6ee7b7',
        tasks: [
          '抽選実行タブで当選者と落選者を確認する',
          'ゲストへ参加可否を連絡する',
          'イベント終了後は下のボタンで状態を最初に戻せる',
        ],
        cta: 'イベントを終了して最初に戻す',
        nextStatus: 'waiting', navigateToMatching: false,
      },
    },
  };

  const guides = guidesByType[eventType] ?? guidesByType.matching;
  return guides[status] || null;
};

const STEPPER_CSS = `
  @keyframes stepRipple {
    0%   { transform: scale(1);   opacity: 0.6; }
    100% { transform: scale(2.4); opacity: 0; }
  }
  @keyframes stepCheckIn {
    0%   { transform: scale(0.5); opacity: 0; }
    60%  { transform: scale(1.2); opacity: 1; }
    100% { transform: scale(1);   opacity: 1; }
  }
`;

const MODES = [
  {
    value: 'cast_select',
    label: 'キャスト選択',
    icon: '🎭',
    desc: 'キャストがゲストを指名',
  },
  {
    value: 'guest_select',
    label: 'ゲスト選択',
    icon: '👤',
    desc: 'ゲストがキャストを指名',
  },
  {
    value: 'both_select',
    label: '両想いマッチング',
    icon: '💞',
    desc: 'お互いの希望でマッチング',
  },
  {
    value: 'random_only',
    label: 'ランダム抽選',
    icon: '🎲',
    desc: '指名なし・完全ランダム',
  },
];

// ─── Phase Guide ─────────────────────────────────────────────────────────────

const PhaseGuide = ({ status, matchingMode, eventType, onCta, hasResults }) => {
  const [hovBtn, setHovBtn] = useState(false);
  const guide = getPhaseGuide(status, matchingMode, eventType, hasResults);
  if (!guide) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {/* Title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: '22px', height: '22px', borderRadius: '6px',
          background: `${guide.color}18`, fontSize: '12px',
        }}>{guide.icon}</span>
        <span style={{ fontSize: '11px', fontWeight: '800', color: guide.color, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {guide.title} — やること
        </span>
      </div>

      {/* Task list */}
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '5px' }}>
        {guide.tasks.map((task, i) => (
          <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '12px', color: '#475569', lineHeight: 1.5 }}>
            <span style={{
              width: '17px', height: '17px', borderRadius: '50%',
              background: `${guide.color}20`, color: guide.color,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '9px', fontWeight: '800', flexShrink: 0, marginTop: '2px',
            }}>{i + 1}</span>
            {task}
          </li>
        ))}
      </ul>

      {/* CTA — below tasks, left-aligned */}
      <div>
        <button
          onMouseEnter={() => setHovBtn(true)}
          onMouseLeave={() => setHovBtn(false)}
          onClick={() => onCta(guide)}
          style={{
            marginTop: '4px',
            padding: '10px 20px',
            borderRadius: '10px',
            border: `1.5px solid ${guide.color}`,
            background: hovBtn ? guide.color : '#fff',
            color: hovBtn ? '#fff' : guide.color,
            fontWeight: '800',
            fontSize: '13px',
            cursor: 'pointer',
            transition: 'all 0.15s',
            boxShadow: hovBtn ? `0 4px 14px ${guide.color}40` : 'none',
            transform: hovBtn ? 'translateY(-1px)' : 'none',
            whiteSpace: 'nowrap',
          }}
        >
          {guide.cta} →
        </button>
      </div>
    </div>
  );
};

// ─── Status Stepper ───────────────────────────────────────────────────────────

const StatusStepper = ({ current, onSelect, steps }) => {
  const currentIdx = steps.findIndex((s) => s.key === current);
  const progressPct = (currentIdx / (steps.length - 1)) * 100;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: STEPPER_CSS }} />

      {/* Step circles + track */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', paddingTop: '4px' }}>

        {/* Background track */}
        <div style={{
          position: 'absolute',
          top: '20px',
          left: '28px',
          right: '28px',
          height: '3px',
          background: '#e8edf5',
          borderRadius: '999px',
          zIndex: 0,
          overflow: 'hidden',
        }}>
          {/* Progress fill — slides on state change */}
          <div style={{
            position: 'absolute',
            top: 0, left: 0, height: '100%',
            width: `${progressPct}%`,
            background: `linear-gradient(90deg, #f59e0b, #6c63ff, #10b981)`,
            backgroundSize: '200% 100%',
            backgroundPosition: `${progressPct}% 0`,
            borderRadius: '999px',
            transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          }} />

          {/* Chevron markers on track */}
          {[33, 67].map((pct) => (
            <div key={pct} style={{
              position: 'absolute',
              top: '-4px',
              left: `${pct}%`,
              transform: 'translateX(-50%)',
              fontSize: '11px',
              color: pct / 100 <= progressPct / 100 ? 'rgba(255,255,255,0.9)' : '#cbd5e1',
              fontWeight: '900',
              lineHeight: 1,
              transition: 'color 0.4s',
              userSelect: 'none',
              pointerEvents: 'none',
            }}>›</div>
          ))}
        </div>

        {/* Step nodes */}
        {steps.map((step, idx) => {
          const isActive = step.key === current;
          const isPast   = idx < currentIdx;

          return (
            <div key={step.key} style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '10px',
              position: 'relative',
              zIndex: 1,
              cursor: 'pointer',
            }}
              onClick={() => onSelect(step.key)}
            >
              {/* Circle with ripple wrapper */}
              <div style={{ position: 'relative', width: '40px', height: '40px' }}>
                {/* Ripple ring — only on active */}
                {isActive && (
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '50%',
                    background: `rgba(${step.pulseColor}, 0.25)`,
                    animation: 'stepRipple 1.8s ease-out infinite',
                    pointerEvents: 'none',
                  }} />
                )}

                {/* Circle button */}
                <div style={{
                  width: '40px', height: '40px',
                  borderRadius: '50%',
                  border: `2.5px solid ${isActive ? step.color : isPast ? step.color : '#e2e8f0'}`,
                  background: isActive ? step.color : isPast ? step.color : '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: isActive ? `0 0 0 4px rgba(${step.pulseColor}, 0.15)` : 'none',
                }}>
                  {isPast ? (
                    <span style={{
                      color: '#fff', fontSize: '14px', fontWeight: '900',
                      animation: 'stepCheckIn 0.3s ease-out',
                    }}>✓</span>
                  ) : (
                    <span style={{
                      color: isActive ? '#fff' : '#cbd5e1',
                      fontSize: '13px', fontWeight: '900',
                      transition: 'color 0.3s',
                    }}>{idx + 1}</span>
                  )}
                </div>
              </div>

              {/* Label */}
              <div style={{ textAlign: 'center', userSelect: 'none' }}>
                <div style={{
                  fontSize: '12px', fontWeight: '800',
                  color: isActive ? step.color : isPast ? '#64748b' : '#94a3b8',
                  marginBottom: '3px',
                  transition: 'color 0.3s',
                }}>
                  {step.label}
                </div>
                <div style={{
                  fontSize: '10px', color: '#94a3b8',
                  whiteSpace: 'nowrap',
                  transition: 'opacity 0.3s',
                  opacity: isActive ? 1 : 0.6,
                }}>
                  {step.desc}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

// ─── Mode Selector ────────────────────────────────────────────────────────────

const ModeSelector = ({ current, onChange }) => {
  const [hovered, setHovered] = useState(null);
  return (
    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
      {MODES.map((m) => {
        const isActive = current === m.value;
        const isHov = hovered === m.value;
        return (
          <button
            key={m.value}
            onClick={() => onChange(m.value)}
            onMouseEnter={() => setHovered(m.value)}
            onMouseLeave={() => setHovered(null)}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              gap: '4px',
              padding: '10px 14px',
              border: `1.5px solid ${isActive ? '#6c63ff' : isHov ? '#c4b5fd' : '#e2e8f0'}`,
              borderRadius: '10px',
              background: isActive ? '#f5f3ff' : isHov ? '#faf9ff' : '#fff',
              cursor: 'pointer',
              transition: 'all 0.15s',
              minWidth: '90px',
            }}
          >
            <span style={{ fontSize: '18px' }}>{m.icon}</span>
            <span style={{
              fontSize: '11px', fontWeight: '800',
              color: isActive ? '#6c63ff' : '#64748b',
            }}>{m.label}</span>
            <span style={{
              fontSize: '10px',
              color: '#94a3b8',
              textAlign: 'center',
              lineHeight: 1.3,
            }}>{m.desc}</span>
          </button>
        );
      })}
    </div>
  );
};

// ─── Instance Settings ────────────────────────────────────────────────────────

const InstanceSettings = ({ event, eventId, onUpdate }) => {
  const mode     = event?.instance_mode || 'count';
  const count    = event?.instance_count ?? '';
  const capacity = event?.instance_capacity ?? '';
  const [editing, setEditing] = useState(false);
  const [editMode, setEditMode] = useState(mode);
  const [editCount, setEditCount] = useState('');
  const [editCapacity, setEditCapacity] = useState('');

  const startEdit = () => {
    setEditMode(mode);
    setEditCount(count);
    setEditCapacity(capacity);
    setEditing(true);
  };

  const save = () => {
    onUpdate(eventId, { instanceMode: editMode, instanceCount: editCount, instanceCapacity: editCapacity });
    setEditing(false);
  };

  const displayLabel = mode === 'count'
    ? (count ? `${count} インスタンス` : '未設定')
    : (capacity ? `1インスタンス ${capacity}人` : '未設定');

  return (
    <div style={{ flexShrink: 0, minWidth: 160 }}>
      <p style={{ margin: '0 0 8px', fontSize: '11px', fontWeight: '700', color: '#94a3b8', letterSpacing: '1px', textTransform: 'uppercase' }}>
        インスタンス設定
      </p>
      {editing ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {[{ v: 'count', l: '数指定' }, { v: 'capacity', l: '人数指定' }].map(({ v, l }) => (
              <button key={v} onClick={() => setEditMode(v)} style={{
                flex: 1, padding: '5px 6px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 700,
                border: `1.5px solid ${editMode === v ? '#6c63ff' : '#e2e8f0'}`,
                background: editMode === v ? '#f5f3ff' : '#fff',
                color: editMode === v ? '#6c63ff' : '#64748b',
              }}>{l}</button>
            ))}
          </div>
          {editMode === 'count' ? (
            <input type="number" min="1" value={editCount}
              onChange={(e) => setEditCount(e.target.value)}
              onKeyDown={(e) => ['e','E','+','-','.'].includes(e.key) && e.preventDefault()}
              placeholder="インスタンス数"
              style={{ padding: '6px 8px', borderRadius: 8, border: '1.5px solid #6c63ff', fontSize: 13, fontWeight: 700, outline: 'none', width: '100%', boxSizing: 'border-box' }}
              autoFocus
            />
          ) : (
            <input type="number" min="1" value={editCapacity}
              onChange={(e) => setEditCapacity(e.target.value)}
              onKeyDown={(e) => ['e','E','+','-','.'].includes(e.key) && e.preventDefault()}
              placeholder="1インスタンスの人数"
              style={{ padding: '6px 8px', borderRadius: 8, border: '1.5px solid #6c63ff', fontSize: 13, fontWeight: 700, outline: 'none', width: '100%', boxSizing: 'border-box' }}
              autoFocus
            />
          )}
          <div style={{ display: 'flex', gap: 4 }}>
            <button onClick={save} style={{ flex: 1, padding: '6px', borderRadius: 8, border: 'none', background: '#6c63ff', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>保存</button>
            <button onClick={() => setEditing(false)} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>✕</button>
          </div>
        </div>
      ) : (
        <button onClick={startEdit} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#fff', cursor: 'pointer' }}>
          <span style={{ fontSize: 16, fontWeight: 900, color: '#6c63ff' }}>{displayLabel}</span>
          <span style={{ fontSize: 11, color: '#94a3b8' }}>✏️</span>
        </button>
      )}
    </div>
  );
};

// ─── Panel ────────────────────────────────────────────────────────────────────

const Panel = ({ accentColor, icon, title, role, stats, headerAction, children }) => (
  <div style={{
    background: '#fff',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 6px 20px rgba(0,0,0,0.05)',
    border: '1px solid #e8edf5',
    display: 'flex',
    flexDirection: 'column',
  }}>
    {/* Colored header band */}
    <div style={{
      background: `${accentColor}0d`,
      borderBottom: `1px solid ${accentColor}22`,
      padding: '16px 20px',
    }}>
      {/* Title row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
            <span style={{ fontSize: '18px' }}>{icon}</span>
            <span style={{ fontSize: '16px', fontWeight: '900', color: '#0f172a' }}>{title}</span>
          </div>
          <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8' }}>{role}</p>
        </div>
        <span style={{
          fontSize: '20px', fontWeight: '900', color: accentColor,
          background: `${accentColor}18`,
          border: `1px solid ${accentColor}30`,
          borderRadius: '8px', padding: '4px 12px',
          lineHeight: 1.3,
        }}>
          {stats.total}
          <span style={{ fontSize: '11px', fontWeight: '600', marginLeft: '2px' }}>人</span>
        </span>
      </div>

      {/* Stat chips row */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
        {stats.items.map(({ label, value, color }) => (
          <div key={label} style={{
            display: 'flex', alignItems: 'center', gap: '5px',
            background: '#fff',
            border: '1px solid #e8edf5',
            borderRadius: '6px',
            padding: '4px 10px',
          }}>
            <span style={{
              display: 'inline-block', width: '7px', height: '7px',
              borderRadius: '50%', background: color, flexShrink: 0,
            }} />
            <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>{label}</span>
            <span style={{ fontSize: '13px', fontWeight: '800', color }}>{value}</span>
          </div>
        ))}
        {headerAction}
      </div>
    </div>

    {/* Content */}
    <div style={{ padding: '16px 20px 20px', flex: 1 }}>
      {children}
    </div>
  </div>
);

// ─── Empty state ──────────────────────────────────────────────────────────────

const Empty = ({ icon, text, hint }) => (
  <div style={{
    textAlign: 'center', padding: '36px 16px',
    color: '#94a3b8',
    border: '1.5px dashed #e2e8f0',
    borderRadius: '10px',
    marginTop: '8px',
  }}>
    <div style={{ fontSize: '30px', marginBottom: '8px', opacity: 0.4 }}>{icon}</div>
    <div style={{ fontWeight: '700', color: '#64748b', marginBottom: '4px', fontSize: '14px' }}>{text}</div>
    <div style={{ fontSize: '12px' }}>{hint}</div>
  </div>
);

// ─── Action buttons ───────────────────────────────────────────────────────────

const DangerBtn = ({ onClick, children }) => {
  const [h, setH] = useState(false);
  return (
    <button onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} onClick={onClick}
      style={{
        background: h ? '#fef2f2' : '#fff',
        border: `1px solid ${h ? '#fca5a5' : '#fecaca'}`,
        color: '#ef4444',
        padding: '9px 16px', borderRadius: '8px', cursor: 'pointer',
        fontWeight: '700', fontSize: '13px', transition: 'all 0.15s',
      }}
    >{children}</button>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────

const EventPage = ({ selectedEventId, onNavigateToMatching }) => {
  const confirm = useConfirm();
  const { events, updateEventStatus, updateMatchingMode, updateCapacity, updateInstanceSettings, clearAllDraftPicks, generateNominationToken, deleteNominationToken } = useEvents();
  const [editingCapacity, setEditingCapacity] = useState(false);
  const [capacityInput, setCapacityInput] = useState('');
  const { clearResults } = useMatchingResults(selectedEventId);
  const { stats: applicantStats, commitOccurrence } = useApplicantStats(selectedEventId);
  const [pickerState, setPickerState] = useState({ isOpen: false, type: null, id: null });
  const [showMatchedOnly, setShowMatchedOnly] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showFormBuilder, setShowFormBuilder] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedNomUrl, setCopiedNomUrl] = useState(false);
  const [generatingNomUrl, setGeneratingNomUrl] = useState(false);

  const { formDef, formToken, applications, saveFormDef, generateToken, deleteToken, clearApplications } = useApplicationForm(selectedEventId);

  // matched 以外のフェーズに戻ったらフィルターをリセット
  useEffect(() => {
    if (events[selectedEventId]?.status !== 'matched') setShowMatchedOnly(false);
  }, [events, selectedEventId]);

  const handleClosePicker = () => setPickerState({ isOpen: false, type: null, id: null });
  const handleModeChange = async (newMode) => {
    if (newMode === (selectedEvent?.matching_mode || 'cast_select')) return;
    await clearAllDraftPicks(selectedEventId);
    updateMatchingMode(selectedEventId, newMode);
  };

  const {
    casts, addCast, updateCast, removeCast, toggleCastStatus, updateCastTags,
    updateCastPriority, updateCastDraftPicks,
  } = useCasts(selectedEventId);

  const { schedule, setAttendance, setSessionAttendance } = useSchedule(selectedEventId);

  // ── 当日同期: schedule.attendance → cast.status ───────────────────────────
  useEffect(() => {
    if (!selectedEventId || !schedule.attendance) return;
    const targetDate = nextEventDate(schedule.dates);
    Object.entries(casts).forEach(([castId, cast]) => {
      const dayStatus = schedule.attendance[castId]?.[targetDate];
      if (dayStatus && cast.status !== dayStatus) {
        updateCast(castId, { status: dayStatus });
      }
    });
  // schedule.attendance と casts が揃ったタイミングで同期
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEventId, schedule.attendance, casts]);

  // ── 手動切替: cast.status と schedule.attendance を双方向同期 ─────────────
  const handleToggleCastStatus = (castId, currentStatus) => {
    const newStatus = currentStatus === 'working' ? 'absent' : 'working';
    toggleCastStatus(castId, currentStatus);
    // 次の開催日分のattendanceも更新
    setAttendance(castId, nextEventDate(schedule.dates), newStatus);
  };

  const {
    guests, addGuest, removeGuest, clearGuests,
    updateGuestPriority, updateGuestDraftPicks,
  } = useGuests(selectedEventId);

  const selectedEvent = selectedEventId ? events[selectedEventId] : null;
  const canViewHistory = useHistoryAccess(selectedEventId, selectedEvent);

  // ── インスタンス設定変更時: 範囲外の pinned_instance を自動クリア ─────────
  useEffect(() => {
    if (!selectedEvent || selectedEvent.event_type !== 'cast_instance') return;
    const instCount = selectedEvent.instance_mode === 'count'
      ? (selectedEvent.instance_count || null)
      : (selectedEvent.instance_capacity
          ? Math.max(1, Math.ceil(
              Object.values(casts).filter((c) => c.status === 'working').length / selectedEvent.instance_capacity
            ))
          : null);
    if (!instCount) return;
    Object.entries(casts).forEach(([castId, cast]) => {
      if (cast.pinned_instance != null && cast.pinned_instance > instCount) {
        updateCast(castId, { pinned_instance: null });
      }
    });
  // インスタンス設定が変わったときだけ走らせる（castの変化では不要）
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent?.instance_count, selectedEvent?.instance_capacity, selectedEvent?.instance_mode]);

  // Pro限定: 前回当選者（このイベントの直近開催で当選）を一括で「最低」に
  const prevWinnerCount = Object.values(guests).filter(
    (g) => applicantStats[guestIdentityKey(g)]?.won_prev
  ).length;
  const handleAutoExcludePrevWinners = async () => {
    const targets = Object.entries(guests).filter(
      ([, g]) => applicantStats[guestIdentityKey(g)]?.won_prev
    );
    if (targets.length === 0) return;
    const ok = await confirm({
      message: `前回開催で当選した ${targets.length}人 を優先度「最低」に設定します。\nよろしいですか？`,
      type: 'info',
    });
    if (!ok) return;
    targets.forEach(([guestId]) => updateGuestPriority(guestId, -1));
  };

  // ── 共通フェーズ遷移（確認・副作用込み）。成功時 true を返す ──────────
  const executePhaseTransition = async (targetStatus) => {
    if (targetStatus === selectedEvent?.status) return false;

    // 準備中に戻す: 結果・応募データのクリア確認
    if (targetStatus === 'waiting') {
      const hasResults = (selectedEvent?.results?.pairs?.length ?? 0) > 0;
      const hasApps    = Object.keys(applications).length > 0;
      if (hasResults || hasApps) {
        const message = (hasResults && hasApps)
          ? '抽選結果と応募データをクリアしてイベントを最初の状態に戻しますか？\nこの操作は元に戻せません。'
          : hasResults
            ? '抽選結果をクリアしてイベントを最初の状態に戻しますか？\nこの操作は元に戻せません。'
            : '応募データをクリアしてイベントを最初の状態に戻しますか？\nこの操作は元に戻せません。';
        if (!(await confirm({ message, type: 'danger' }))) return false;
      }
      // 抽選完了(matched)→準備中(waiting) の時だけ、消す前に最終状態を履歴へ確定
      if (selectedEvent?.status === 'matched') {
        await commitOccurrence(guests, selectedEvent?.results);
      }
      if (hasResults) {
        clearResults();
      }
      clearGuests();
      await clearApplications();
      if (selectedEvent?.status === 'matching') await deleteToken(formToken);
    }

    // → matching: トークン生成（cast_instance は応募フォーム不要なのでスキップ）
    if (targetStatus === 'matching' && !isCastInstanceType) await generateToken();
    // matching →: トークン削除
    if (selectedEvent?.status === 'matching' && targetStatus !== 'matching' && !isCastInstanceType) await deleteToken(formToken);

    updateEventStatus(selectedEventId, targetStatus);
    return true;
  };

  // 指名URL ハンドラ
  const handleGenerateNomUrl = async () => {
    const castNames  = Object.values(selectedEvent?.casts  || {}).map(c => (c.vrchat_name || c.name || '').trim().toLowerCase()).filter(Boolean);
    const guestNames = Object.values(selectedEvent?.guests || {}).map(g => (g.vrchat_name || '').trim().toLowerCase()).filter(Boolean);
    const dups = castNames.filter(n => guestNames.includes(n));
    if (dups.length > 0) {
      await confirm({ message: `キャストとゲストに同じVRChat名があります：${dups.join('、')}\n先に重複を解消してください`, type: 'danger' });
      return;
    }
    setGeneratingNomUrl(true);
    const newToken = await generateNominationToken(selectedEventId);
    setGeneratingNomUrl(false);
    navigator.clipboard.writeText(`${window.location.origin}/nominate/${newToken}`).then(() => {
      setCopiedNomUrl(true);
      setTimeout(() => setCopiedNomUrl(false), 2000);
    });
  };
  const handleCopyNomUrl = () => {
    const token = selectedEvent?.nomination_token;
    if (!token) return;
    navigator.clipboard.writeText(`${window.location.origin}/nominate/${token}`);
    setCopiedNomUrl(true);
    setTimeout(() => setCopiedNomUrl(false), 2000);
  };
  const handleInvalidateNomUrl = () => {
    deleteNominationToken(selectedEventId, selectedEvent?.nomination_token);
  };

  // フェーズCTAボタン
  const handlePhaseCta = async (guide) => {
    await executePhaseTransition(guide.nextStatus);
    if (guide.navigateToMatching && onNavigateToMatching) {
      setTimeout(() => onNavigateToMatching(), 150);
    }
  };

  // ステッパークリック（1段飛ばし前進を禁止）
  const handleStepperSelect = async (targetStatus) => {
    const currentIdx = statusSteps.findIndex(s => s.key === selectedEvent?.status);
    const targetIdx  = statusSteps.findIndex(s => s.key === targetStatus);
    if (targetIdx - currentIdx > 1) return; // 2段以上の前進は不可
    await executePhaseTransition(targetStatus);
  };

  if (!selectedEvent) {
    return (
      <div style={{ minHeight: '100%', background: '#f8fafc', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
        <span style={{ fontSize: '40px', opacity: 0.2 }}>📋</span>
        <p style={{ margin: 0, color: '#94a3b8', fontWeight: '600' }}>イベントが選択されていません</p>
      </div>
    );
  }

  const castArray     = Object.entries(casts);
  const guestArray    = Object.entries(guests);
  const workingCount  = castArray.filter(([, c]) => c.status === 'working').length;
  const absentCount   = castArray.filter(([, c]) => c.status === 'absent').length;
  const waitingGuests = guestArray.filter(([, g]) => g.status === 'waiting').length;
  const matchedGuests = guestArray.filter(([, g]) => g.status === 'matched').length;
  const currentMode        = selectedEvent?.matching_mode || 'cast_select';
  const eventType          = selectedEvent?.event_type || 'matching';
  const isMatchingType     = eventType === 'matching';
  const isCastInstanceType = eventType === 'cast_instance';
  const isNominationMode   = isMatchingType && currentMode !== 'random_only';
  const statusSteps   = getStatusSteps(eventType);

  // cast_instance: UIに表示するセレクター用の実効インスタンス数
  const effectiveInstanceCount = isCastInstanceType
    ? (selectedEvent?.instance_mode === 'count'
        ? (selectedEvent?.instance_count || null)
        : (selectedEvent?.instance_capacity
            ? Math.max(1, Math.ceil(workingCount / selectedEvent.instance_capacity))
            : null))
    : null;

  const sessions           = schedule.sessions ?? null;
  const session_attendance = schedule.session_attendance || {};
  const today              = nextEventDate(schedule.dates);
  const sessionCounts = sessions
    ? Array.from({ length: sessions }, (_, i) => {
        const ses = i + 1;
        return castArray.filter(([castId]) => session_attendance[castId]?.[today]?.[ses] === 'working').length;
      })
    : null;
  const unassignedCount = sessions
    ? castArray.filter(([castId]) =>
        !Object.values(session_attendance[castId]?.[today] || {}).some(s => s === 'working')
      ).length
    : null;

  return (
    <div style={{ padding: '24px', background: '#f8fafc', minHeight: '100%', boxSizing: 'border-box' }}>

      {/* ── HEADER ──────────────────────────────────────────────────────── */}
      <div style={{
        background: '#fff',
        borderRadius: '16px',
        padding: '22px 26px',
        marginBottom: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 6px 20px rgba(0,0,0,0.04)',
        border: '1px solid #e8edf5',
      }}>
        {/* Event name */}
        <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <p style={{ margin: '0 0 5px', fontSize: '11px', fontWeight: '700', color: '#94a3b8', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
              イベント管理
            </p>
            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '900', color: '#0f172a', letterSpacing: '-0.4px' }}>
              {selectedEvent.event_name}
            </h1>
          </div>

          {/* 応募フォームボタン群（cast_instance は不要） */}
          {!isCastInstanceType && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* 準備中: フォーム作成/編集ボタン */}
              {selectedEvent.status === 'waiting' && (
                <button
                  onClick={() => setShowFormBuilder(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: '1.5px solid #6c63ff', background: '#f5f3ff', color: '#6c63ff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                >
                  📋 {(formDef?.fields?.length ?? 0) > 0 ? 'フォームを編集' : 'フォームを作成'}
                </button>
              )}

              {/* 応募受付中: URLコピーボタン */}
              {selectedEvent.status === 'matching' && formToken && (
                <button
                  onClick={() => {
                    const url = `${window.location.origin}/apply/${formToken}`;
                    navigator.clipboard.writeText(url).then(() => {
                      setCopiedUrl(true);
                      setTimeout(() => setCopiedUrl(false), 2000);
                    });
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: `1.5px solid ${copiedUrl ? '#10b981' : '#6c63ff'}`, background: copiedUrl ? '#ecfdf5' : '#f5f3ff', color: copiedUrl ? '#059669' : '#6c63ff', fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s' }}
                >
                  {copiedUrl ? '✓ コピー完了！' : '🔗 応募URLをコピー'}
                </button>
              )}

              {/* 指名設定フェーズ: 指名URLボタン */}
              {isNominationMode && selectedEvent.status === 'matched' && (
                selectedEvent.nomination_token ? (
                  <>
                    <button
                      onClick={handleCopyNomUrl}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: `1.5px solid ${copiedNomUrl ? '#10b981' : '#8b5cf6'}`, background: copiedNomUrl ? '#ecfdf5' : '#f5f3ff', color: copiedNomUrl ? '#059669' : '#7c3aed', fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s' }}
                    >
                      {copiedNomUrl ? '✓ コピー完了！' : '📝 指名URLをコピー'}
                    </button>
                    <button
                      onClick={handleInvalidateNomUrl}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: '1.5px solid #fca5a5', background: '#fff', color: '#ef4444', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                    >
                      指名を締め切る
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleGenerateNomUrl}
                    disabled={generatingNomUrl}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: '1.5px solid #8b5cf6', background: '#f5f3ff', color: '#7c3aed', fontSize: 12, fontWeight: 700, cursor: generatingNomUrl ? 'not-allowed' : 'pointer', opacity: generatingNomUrl ? 0.7 : 1 }}
                  >
                    {generatingNomUrl ? '発行中...' : '📝 指名URLを発行'}
                  </button>
                )
              )}
            </div>
          )}
        </div>

        {/* Status stepper — full width */}
        <div style={{ marginBottom: '20px' }}>
          <p style={{ margin: '0 0 12px', fontSize: '11px', fontWeight: '700', color: '#94a3b8', letterSpacing: '1px', textTransform: 'uppercase' }}>
            イベントの進行状態&nbsp;&nbsp;左 → 右の順に進みます
          </p>
          <StatusStepper
            current={selectedEvent.status}
            onSelect={handleStepperSelect}
            steps={statusSteps}
          />
        </div>

        {/* Bottom row: phase guide (left) + mode selector (right) */}
        <div style={{
          display: 'flex',
          gap: '20px',
          alignItems: 'flex-start',
          paddingTop: '18px',
          borderTop: '1px solid #f1f5f9',
          flexWrap: 'wrap',
        }}>
          {/* Phase guide — takes remaining space */}
          <div style={{ flex: 1, minWidth: '260px' }}>
            <PhaseGuide
              status={selectedEvent.status}
              matchingMode={currentMode}
              eventType={eventType}
              onCta={handlePhaseCta}
              hasResults={!!(selectedEvent?.results?.pairs?.length > 0)}
            />
          </div>

          {/* matching: マッチング方式セレクター */}
          {isMatchingType && (
            <div style={{ flexShrink: 0 }}>
              <p style={{ margin: '0 0 8px', fontSize: '11px', fontWeight: '700', color: '#94a3b8', letterSpacing: '1px', textTransform: 'uppercase' }}>
                マッチング方式
              </p>
              <ModeSelector current={currentMode} onChange={handleModeChange} />
            </div>
          )}

          {/* cast_instance: インスタンス設定 */}
          {isCastInstanceType && (
            <InstanceSettings
              event={selectedEvent}
              eventId={selectedEventId}
              onUpdate={updateInstanceSettings}
            />
          )}

          {/* raffle / open: 定員表示・編集 */}
          {!isMatchingType && !isCastInstanceType && (
            <div style={{ flexShrink: 0, minWidth: 120 }}>
              <p style={{ margin: '0 0 8px', fontSize: '11px', fontWeight: '700', color: '#94a3b8', letterSpacing: '1px', textTransform: 'uppercase' }}>
                定員
              </p>
              {editingCapacity ? (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input
                    type="number"
                    min="1"
                    value={capacityInput}
                    onChange={(e) => setCapacityInput(e.target.value)}
                    style={{ width: 72, padding: '6px 8px', borderRadius: 8, border: '1.5px solid #6c63ff', fontSize: 14, fontWeight: 700, outline: 'none' }}
                    autoFocus
                  />
                  <button
                    onClick={() => { updateCapacity(selectedEventId, capacityInput); setEditingCapacity(false); }}
                    style={{ padding: '6px 10px', borderRadius: 8, border: 'none', background: '#6c63ff', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
                  >保存</button>
                  <button
                    onClick={() => setEditingCapacity(false)}
                    style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
                  >✕</button>
                </div>
              ) : (
                <button
                  onClick={() => { setCapacityInput(selectedEvent?.capacity ?? ''); setEditingCapacity(true); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#fff', cursor: 'pointer' }}
                >
                  <span style={{ fontSize: 20, fontWeight: 900, color: '#6c63ff' }}>
                    {selectedEvent?.capacity ?? '—'}
                  </span>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>人 ✏️</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── ACTION BAR ──────────────────────────────────────────────────── */}
      {!isCastInstanceType && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', alignItems: 'center' }}>
          <div style={{ flex: 1 }} />
          <DangerBtn onClick={async () => {
            if (await confirm({ message: 'ゲストを全員削除しますか？', type: 'danger' })) clearGuests();
          }}>
            ゲスト全削除
          </DangerBtn>
        </div>
      )}

      {/* ── PANELS ──────────────────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '16px',
        alignItems: 'start',
      }}>

        {/* CAST */}
        <Panel
          accentColor="#6c63ff"
          icon="🎭"
          title={isMatchingType ? 'キャスト' : 'スタッフ・キャスト'}
          role={isMatchingType ? 'イベントに出演するキャストを管理します' : '出退勤を管理するスタッフ・キャストを登録します'}
          stats={sessions ? {
            total: castArray.length,
            items: [
              ...sessionCounts.map((count, i) => ({ label: `第${i + 1}部`, value: count, color: '#6c63ff' })),
              { label: '未設定', value: unassignedCount, color: '#94a3b8' },
            ],
          } : {
            total: castArray.length,
            items: [
              { label: '出勤中',  value: workingCount, color: '#6c63ff' },
              { label: '未出勤',  value: absentCount,  color: '#f87171' },
            ],
          }}
        >
          <CastForm onAdd={addCast} existingCasts={casts} />
          {castArray.length === 0 ? (
            <Empty
              icon="🎭"
              text={isMatchingType ? 'キャストがまだ登録されていません' : 'スタッフがまだ登録されていません'}
              hint="上のフォームから追加してください"
            />
          ) : (
            <CastList
              casts={casts}
              onRemove={removeCast}
              onToggleStatus={handleToggleCastStatus}
              onUpdateTags={updateCastTags}
              onUpdatePriority={updateCastPriority}
              onUpdate={updateCast}
              onOpenPicker={(id) => setPickerState({ isOpen: true, type: 'cast', id })}
              matchingMode={isMatchingType ? currentMode : 'random_only'}
              sessions={sessions}
              sessionAttendance={session_attendance}
              today={today}
              onSetSessionAttendance={(castId, ses, status) => setSessionAttendance(castId, today, ses, status)}
              showLeader={isCastInstanceType}
              onToggleLeader={(castId, value) => updateCast(castId, { is_instance_leader: value })}
              instanceCount={effectiveInstanceCount}
              onSetPinnedInstance={(castId, value) => updateCast(castId, { pinned_instance: value ?? null })}
            />
          )}
        </Panel>

        {/* GUEST（cast_instance は非表示） */}
        {!isCastInstanceType && (<Panel
          accentColor="#10b981"
          icon="👤"
          title="ゲスト"
          role="来場するゲストを管理します"
          stats={{
            total: guestArray.length,
            items: [
              { label: '待機中',  value: waitingGuests, color: '#f59e0b' },
              { label: 'マッチ済', value: matchedGuests, color: '#10b981' },
            ],
          }}
          headerAction={(canViewHistory || selectedEvent?.status === 'matched') && (
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {canViewHistory && prevWinnerCount > 0 && (
                <button
                  onClick={handleAutoExcludePrevWinners}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '5px',
                    padding: '4px 10px',
                    border: '1px solid #e2e8f0', borderRadius: '6px',
                    background: '#f8fafc', color: '#475569',
                    fontSize: '11px', fontWeight: '700', cursor: 'pointer',
                    transition: 'all 0.15s', whiteSpace: 'nowrap',
                  }}
                  title="前回開催で当選したゲストを優先度「最低」に一括設定します"
                >
                  ▽ 前回当選者を「最低」に（{prevWinnerCount}）
                </button>
              )}
              {canViewHistory && (
                <button
                  onClick={() => setShowHistory(true)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '5px',
                    padding: '4px 10px',
                    border: '1px solid #ddd6fe', borderRadius: '6px',
                    background: '#f5f3ff', color: '#6c63ff',
                    fontSize: '11px', fontWeight: '700', cursor: 'pointer',
                    transition: 'all 0.15s', whiteSpace: 'nowrap',
                  }}
                >
                  📊 応募者履歴
                </button>
              )}
              {selectedEvent?.status === 'matched' && (
                <button
                  onClick={() => setShowMatchedOnly(v => !v)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '5px',
                    padding: '4px 10px',
                    border: `1px solid ${showMatchedOnly ? '#10b981' : '#e2e8f0'}`,
                    borderRadius: '6px',
                    background: showMatchedOnly ? '#ecfdf5' : '#fff',
                    color: showMatchedOnly ? '#059669' : '#64748b',
                    fontSize: '11px', fontWeight: '700', cursor: 'pointer',
                    transition: 'all 0.15s', whiteSpace: 'nowrap',
                  }}
                >
                  {showMatchedOnly ? '✓ マッチ済みのみ' : 'マッチ済みのみ表示'}
                </button>
              )}
            </div>
          )}
        >
          <GuestForm onAdd={addGuest} existingGuests={guests} />
          {guestArray.length === 0 ? (
            <Empty
              icon="👤"
              text="ゲストがまだ登録されていません"
              hint="上のフォームからゲストを追加してください"
            />
          ) : (
            <GuestList
              guests={guests}
              onRemove={removeGuest}
              onUpdatePriority={updateGuestPriority}
              onOpenPicker={(id) => setPickerState({ isOpen: true, type: 'guest', id })}
              matchingMode={currentMode}
              showMatchedOnly={showMatchedOnly}
              stats={applicantStats}
              canViewHistory={canViewHistory}
            />
          )}
        </Panel>)}
      </div>

      <ApplicantHistoryModal
        open={showHistory}
        onClose={() => setShowHistory(false)}
        stats={applicantStats}
        eventName={selectedEvent?.event_name}
      />

      <DraftPickerModal
        isOpen={pickerState.isOpen}
        onClose={handleClosePicker}
        title={
          pickerState.type === 'cast'
            ? `${casts[pickerState.id]?.name} の指名リスト設定`
            : `${guests[pickerState.id]?.vrchat_name} の指名リスト設定`
        }
        currentPicks={
          pickerState.type === 'cast'
            ? casts[pickerState.id]?.draft_picks || []
            : guests[pickerState.id]?.draft_picks || []
        }
        availableTargets={pickerState.type === 'cast' ? guests : casts}
        onSave={(newPicks) => {
          if (pickerState.type === 'cast') updateCastDraftPicks(pickerState.id, newPicks);
          else updateGuestDraftPicks(pickerState.id, newPicks);
        }}
      />

      {/* FormBuilder モーダル */}
      {showFormBuilder && (
        <FormBuilder
          formDef={formDef}
          onSave={saveFormDef}
          onClose={() => setShowFormBuilder(false)}
        />
      )}
    </div>
  );
};

export default EventPage;
